import { type FC, useCallback, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { assignIdentifierOptions } from '@/components/Modals/AddTask/Steps/AddTaskExecutionForm.tsx';
import { PercentChangeFromOptions } from '@/components/Modals/SetupWorkflow/SetupWorkflow.model';
import type { SetupWorkflowFormValues } from '@/components/Modals/SetupWorkflow/SetupWorkflow';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import DraggableList, { asSortableListItem } from '@/components/UI/List/DraggableList';
import Select from '@/components/UI/Select';
import { _isEmpty } from '@/littledash';

import { assembleToggleOptions, WORKFLOW_TAB_INDEX, workflowTabPath } from '../SetupWorkflow.utils';

const measurementsPathInFormState = workflowTabPath(WORKFLOW_TAB_INDEX.MEASUREMENTS);
const measurementsChildrenPath = `${measurementsPathInFormState}.children` as const;

export type MeasurementOption = {
  label: string;
  value: string;
};
const WorkflowMeasurements: FC = () => {
  const { control, setValue } = useFormContext<SetupWorkflowFormValues>();
  const { fields: measurementFields, move } = useFieldArray<
    SetupWorkflowFormValues,
    typeof measurementsChildrenPath,
    'fieldId'
  >({
    control,
    keyName: 'fieldId',
    name: measurementsChildrenPath,
  });
  const percentChangeOptions = PercentChangeFromOptions;

  const measurementChildren = useWatch({
    control,
    name: measurementsChildrenPath,
  });

  const selectedMeasurements = useMemo<Array<MeasurementOption>>(
    () =>
      measurementChildren?.reduce<Array<MeasurementOption>>((acc, field) => {
        if (field.value) {
          acc.push({ value: field.id, label: field.displayName });
        }
        return acc;
      }, []) ?? [],
    [measurementChildren]
  );

  const workflowTabConfig = useWatch({
    control,
    name: 'workflowSetupForm.workflowTabConfig',
  });

  const toggleOptions = useMemo(
    () => assembleToggleOptions(workflowTabConfig ?? [], 'measurements'),
    [workflowTabConfig]
  );

  const toggleAllMeasurements = useCallback(
    (selected = true) => {
      measurementFields.forEach((_, index) => {
        setValue(`${measurementsChildrenPath}.${index}.value`, selected);
      });
    },
    [measurementFields, setValue]
  );

  return (
    <>
      <div className="pa3" style={{ height: 375 }}>
        <div className="pb3">
          <a className="link blue f7 dib" onClick={() => toggleAllMeasurements()}>
            Select all
          </a>
          <a className="link blue f7 dib ml3" onClick={() => toggleAllMeasurements(false)}>
            Clear selection
          </a>
        </div>
        <DraggableList
          canAmend={false}
          data={measurementFields}
          sortable={{
            droppableId: 'workflow-setup-measurement',
            onReorder: move,
          }}
          itemsContainerProps={{ className: 'overflow-auto', style: { maxHeight: 330 } }}
          ItemsContainerType="div"
          renderItem={asSortableListItem({ canSort: true, itemClassName: 'mb3' })(FormItem)}
        />
      </div>
      <div className="pa3 bt b--moon-gray flex justify-between items-center">
        <label className="mb0 f6">Change default cursor position</label>
        <Controller
          name={`${measurementsPathInFormState}.cursorPosition`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              options={selectedMeasurements}
              defaultValue={selectedMeasurements?.find((option) => option?.value === value)}
              placeholder="None"
              onChange={(option) => onChange(option?.value ?? null)}
              data-testid="assign-default-cursor-select"
              disabled={_isEmpty(selectedMeasurements)}
              isClearable
              isSearchable
              isMulti={false}
            />
          )}
        />
      </div>
      <div
        className="pa3 bt b--moon-gray flex justify-between items-center"
        data-testid="weight-percentage-change-select"
      >
        <label className="mb0 f6">Weight percentage change from</label>
        <Controller
          name={`${measurementsPathInFormState}.percentChange.weight`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              options={percentChangeOptions}
              defaultValue={percentChangeOptions.find((option) => option.value === value)}
              onChange={(option) => onChange(option?.value)}
              isSearchable
              isMulti={false}
            />
          )}
        />
      </div>
      <div
        style={{ height: '12%' }}
        className="pa3 bt b--moon-gray flex justify-between items-center"
        data-testid="assign-identifiers-select"
      >
        <label className="mb0 f6">Assign identifiers</label>
        <Controller
          control={control}
          name={`${measurementsPathInFormState}.altIdToAssign`}
          render={({ field: { value, onChange } }) => (
            <Select
              options={assignIdentifierOptions}
              defaultValue={assignIdentifierOptions?.find((option) => option?.value === value)}
              placeholder="None"
              onChange={(option) => onChange(option?.value ?? null)}
              isClearable
              isSearchable
              isMulti={false}
            />
          )}
        />
      </div>
      <div
        className="pa3 bt b--moon-gray flex justify-between items-center"
        data-testid="workflow-measurements-after-save-select"
      >
        <label className="mb0 f6">After saving</label>
        <Controller
          defaultValue="focus-search"
          name={`${measurementsPathInFormState}.onSave`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <select className="mw5 mb0" value={value} onChange={onChange}>
              <option value="focus-search" data-testid="after-saving-option-focus-search-bar">
                Focus search bar
              </option>
              {Object.keys(toggleOptions).map((k) => (
                <option
                  key={k}
                  value={toggleOptions[k].value}
                  data-testid={`after-saving-option-${toggleOptions[k].value}`}
                >
                  {toggleOptions[k].name}
                </option>
              ))}
              <option value="next-subject" data-testid="after-saving-option-move-to-next-animal">
                Move to next animal
              </option>
            </select>
          )}
        />
      </div>
    </>
  );
};

interface FormItemProps {
  fieldId?: string;
  id: string;
  displayName: string;
  value?: unknown;
  index: number;
}

const FormItem: FC<FormItemProps> = ({ fieldId, id, displayName, value, index }) => {
  const { control } = useFormContext<SetupWorkflowFormValues>();

  return (
    <Controller
      key={fieldId}
      defaultValue={value as boolean}
      name={`${measurementsChildrenPath}.${index}.value`}
      control={control}
      render={({ field: { onChange, value } }) => {
        const isChecked = value === true;
        return (
          <div className="flex justify-between items-center">
            <Checkbox
              className="pointer pv1"
              label={displayName}
              name={id}
              checked={isChecked}
              onChange={() => onChange(!isChecked)}
            />
          </div>
        );
      }}
    />
  );
};

export default WorkflowMeasurements;
